import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Tabs,
	Tab,
	Typography,
	Chip,
	Checkbox,
	FormControlLabel,
	Alert,
	Button,
} from "@mui/material";
import moment from "moment";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";

// ─── Validation Schemas

const resolveSchema = yup.object().shape({
	resolvedAt: yup.string().trim().required("Resolved date/time is required"),
	resolvedComments: yup
		.string()
		.trim()
		.max(500, maxLengthMessage(500))
		.nullable(),
});

const workOrderSchema = yup.object().shape({
	type: yup.string().trim().required("Type is required"),
	vendor: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	cost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	notes: yup.string().trim().max(1000, maxLengthMessage(1000)).nullable(),
	fundingSourceID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
});

// ─── Helpers

const TabPanel = ({ children, value, index }) =>
	value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const getStatusChipColor = (status) => {
	switch (status) {
		case "Pass":
			return "success";
		case "Fail":
			return "error";
		case "Needs Attention":
			return "warning";
		case "N/A":
			return "default";
		default:
			return "default";
	}
};

const getResultIcon = (status) => {
	switch (status) {
		case "Pass":
			return "✅";
		case "Fail":
			return "🚫";
		case "Needs Attention":
			return "⚠️";
		default:
			return "";
	}
};

// ─── Resolve Inspection Sub-Dialog

const ResolveInspectionDialog = ({ open, onClose, inspection, onSave }) => {
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(resolveSchema),
		defaultValues: {
			resolvedAt: "",
			resolvedComments: "",
		},
		mode: "onChange",
	});

	useEffect(() => {
		if (open && inspection) {
			reset({
				resolvedAt: inspection.resolvedAt
					? moment(inspection.resolvedAt).format("YYYY-MM-DDTHH:mm")
					: moment().format("YYYY-MM-DDTHH:mm"),
				resolvedComments: inspection.resolvedComments || "",
			});
		}
	}, [open, inspection, reset]);

	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	const onSubmit = (data) => {
		onSave({
			resolvedAt: data.resolvedAt,
			resolvedComments: data.resolvedComments?.trim() || null,
		});
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				{inspection?.resolvedAt
					? "Edit Resolution"
					: "Resolve Inspection"}
			</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "1fr",
						gap: 2,
						pt: 2,
					}}
				>
					<Box>
						<TextFieldController
							name="resolvedAt"
							label="Resolved Date & Time *"
							control={control}
							errors={errors}
							type="datetime-local"
							InputLabelProps={{ shrink: true }}
						/>
					</Box>
					<Box>
						<TextFieldController
							name="resolvedComments"
							label="Resolution Comments"
							control={control}
							errors={errors}
							multiline
							rows={3}
							placeholder="Describe the corrective action taken..."
						/>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATCloseButton onClick={onClose} tooltipText="Cancel" />
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					tooltipText="Save Resolution"
					disabled={!isValid}
				/>
			</DialogActions>
		</Dialog>
	);
};

// ─── Create Work Order Sub-Dialog

const CreateWorkOrderDialog = ({
	open,
	onClose,
	vehicle,
	checklistItems,
	failItems,
	workOrderTypeOptions,
	fundingSourceOptions,
	onSave,
}) => {
	const [flagVehicle, setFlagVehicle] = useState(false);

	const buildDefaultNotes = () => {
		if (!failItems || failItems.length === 0) return "";
		return `Inspection failures:\n${failItems
			.map((f) => {
				const item = checklistItems?.find(
					(c) => c.id === f.checklistItemId,
				);
				return `• ${item?.name || f.checklistItemId}${f.comment ? `: ${f.comment}` : ""}`;
			})
			.join("\n")}`;
	};

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(workOrderSchema),
		defaultValues: {
			type: "",
			vendor: "",
			cost: "",
			notes: "",
			fundingSourceID: "",
		},
		mode: "onChange",
	});

	useEffect(() => {
		if (open) {
			reset({
				type: workOrderTypeOptions?.[0]?.value || "Repair",
				vendor: "",
				cost: "",
				notes: buildDefaultNotes(),
				fundingSourceID: "",
			});
			setFlagVehicle(failItems?.length > 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, reset, workOrderTypeOptions]);

	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	const onSubmit = (data) => {
		onSave({
			type: data.type,
			vendor: data.vendor?.trim() || null,
			cost: data.cost || null,
			notes: data.notes?.trim() || null,
			fundingSourceID: data.fundingSourceID || null,
			flagVehicle,
		});
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				🔧 Create Work Order — {vehicle?.unitNumber || vehicle?.label}
			</DialogTitle>
			<DialogContent>
				{failItems?.length > 0 && (
					<Alert severity="error" sx={{ mb: 2, mt: 1 }}>
						<strong>Inspection Failed</strong> — {failItems.length}{" "}
						failed item
						{failItems.length !== 1 ? "s" : ""} pre-filled in notes
						below.
					</Alert>
				)}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(2, 1fr)",
						gap: 2,
						pt: failItems?.length > 0 ? 0 : 1,
					}}
				>
					<Box>
						<DropdownController
							name="type"
							label="Type *"
							options={
								workOrderTypeOptions?.map((t) => ({
									value: t.value || t,
									label: t.label || t,
								})) || []
							}
							control={control}
							errors={errors}
						/>
					</Box>
					<Box>
						<TextFieldController
							name="vendor"
							label="Vendor"
							control={control}
							errors={errors}
							placeholder="Repair shop / vendor"
						/>
					</Box>
					<Box>
						<TextFieldController
							name="cost"
							label="Cost ($)"
							control={control}
							errors={errors}
							type="number"
							placeholder="0.00"
						/>
					</Box>
					<Box>
						<DropdownController
							name="fundingSourceID"
							label="Funding Source"
							options={
								fundingSourceOptions?.map((fs) => ({
									value: fs.value,
									label: fs.label,
								})) || []
							}
							control={control}
							errors={errors}
							placeholder="— None —"
						/>
					</Box>
					<Box sx={{ gridColumn: "1 / -1" }}>
						<TextFieldController
							name="notes"
							label="Notes"
							control={control}
							errors={errors}
							multiline
							rows={3}
						/>
					</Box>
					{vehicle &&
						vehicle.status !== "Out of Service" &&
						vehicle.status !== "Retired" && (
							<Box sx={{ gridColumn: "1 / -1" }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={flagVehicle}
											onChange={(e) =>
												setFlagVehicle(e.target.checked)
											}
										/>
									}
									label={
										<Typography variant="body2">
											Set vehicle status to{" "}
											<strong>Maintenance</strong>{" "}
											(currently: {vehicle.status})
										</Typography>
									}
								/>
							</Box>
						)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATCloseButton onClick={onClose} tooltipText="Cancel" />
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					tooltipText="Create Work Order"
					disabled={!isValid}
				/>
			</DialogActions>
		</Dialog>
	);
};

// ─── Main Inspection Detail Dialog

const InspectionDetailDialog = ({
	open,
	onClose,
	inspection,
	vehicles = [],
	employees = [],
	checklistItems = [],
	fundingSources = [],
	onResolveInspection,
	onCreateWorkOrder,
	workOrderTypeOptions = [],
	fundingSourceOptions = [],
}) => {
	const [tab, setTab] = useState(0);
	const [showResolve, setShowResolve] = useState(false);
	const [showWO, setShowWO] = useState(false);

	useEffect(() => {
		if (!open) {
			setTab(0);
			setShowResolve(false);
			setShowWO(false);
		}
	}, [open]);

	if (!inspection) return null;

	// ── Derived data ───
	const vehicle = vehicles.find(
		(v) =>
			v.value === inspection.vehicleId || v.id === inspection.vehicleId,
	);
	const employee = employees.find(
		(o) =>
			o.value === inspection.perPersonnelId ||
			o.value === String(inspection.perPersonnelId),
	);

	const allResponses = (inspection.responses || []).map((r) => {
		const item = checklistItems.find((i) => i.id === r.checklistItemId);
		return { ...r, name: item?.name || r.checklistItemId };
	});

	const failItems = allResponses.filter((r) => r.status === "Fail");
	const passItems = allResponses.filter((r) => r.status === "Pass");
	const naItems = allResponses.filter((r) => r.status === "N/A");

	const isFailed =
		inspection.overallStatus === "Fail" ||
		inspection.overallStatus === "Needs Attention";
	const hasPhotos = Array.isArray(inspection.photos) && inspection.photos.length > 0;

	// ── Handlers ───────

	const handleResolve = (resolveData) => {
		if (onResolveInspection) {
			onResolveInspection(resolveData);
		}
	};

	const handleCreateWO = (woData) => {
		if (onCreateWorkOrder) {
			onCreateWorkOrder(woData);
		}
	};

	const handlePrint = () => {
		const d = (v) => v || "—";
		const statusIcon = (r) =>
			r.status === "Pass" ? "✓" : r.status === "Fail" ? "✗" : "N/A";
		const statusColor = (r) =>
			r.status === "Pass"
				? "#166534"
				: r.status === "Fail"
					? "#991b1b"
					: "#6b7280";

		const printResponses = allResponses.map((r) => ({
			name: r.name,
			status: r.status,
			comment: r.comment || "",
		}));

		const vehicleDisplay = vehicle?.label || vehicle?.unitNumber || "—";
		const employeeDisplay = employee?.label || "—";

		const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Inspection Report — ${vehicleDisplay}</title>
<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:11px;color:#1a1a1a;padding:24px}
h1{font-size:18px;font-weight:800;margin-bottom:2px}.subtitle{font-size:12px;color:#555;margin-bottom:4px}.meta{font-size:10px;color:#888;margin-bottom:20px}
.sh{background:#1a1a1a;color:#fff;padding:5px 10px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;margin:16px 0 8px;border-radius:3px}
.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:6px 16px;margin-bottom:8px}
.fl{font-size:9px;font-weight:700;text-transform:uppercase;color:#888;letter-spacing:.06em;margin-bottom:1px}.fv{font-size:11px}
.resp-row{display:flex;align-items:flex-start;gap:10px;padding:4px 6px;border-bottom:1px solid #eee}
.resp-row:nth-child(even){background:#fafafa}.resp-status{font-size:10px;font-weight:700;width:28px;flex-shrink:0;padding-top:1px}
.resp-name{flex:1;font-size:10px}.resp-comment{font-size:9px;color:#888;font-style:italic;margin-top:1px}
.summary-row{display:flex;gap:20px;margin-bottom:12px}.stat{text-align:center;padding:8px 16px;border:1px solid #ddd;border-radius:4px}
.stat-num{font-size:18px;font-weight:800}.stat-label{font-size:9px;text-transform:uppercase;color:#888}
.notes-box{padding:10px 12px;background:#f8f8f8;border-left:3px solid #ddd;font-size:11px;line-height:1.5}
.footer{position:fixed;bottom:0;left:0;right:0;border-top:1px solid #ddd;padding:4px 12px;font-size:9px;color:#aaa;background:#fff;display:flex;justify-content:space-between}
@media print{body{padding:12px}@page{margin:14mm 12mm 20mm}.sh{-webkit-print-color-adjust:exact;print-color-adjust:exact}.resp-row:nth-child(even){-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<h1>Inspection Report — ${vehicleDisplay}</h1>
<div class="subtitle">${d(inspection.type)} · <strong style="color:${inspection.overallStatus === "Pass" ? "#166534" : inspection.overallStatus === "Fail" ? "#991b1b" : "#b45309"}">${d(inspection.overallStatus)}</strong></div>
<div class="meta">Printed ${new Date().toLocaleString()}</div>
<div class="sh">Inspection Details</div>
<div class="grid">
  <div><div class="fl">Date / Time</div><div class="fv">${d(moment(inspection.date).format("MM/DD/YYYY hh:mm A"))}</div></div>
  <div><div class="fl">Type</div><div class="fv">${d(inspection.type)}</div></div>
  <div><div class="fl">Overall Result</div><div class="fv">${d(inspection.overallStatus)}</div></div>
  <div><div class="fl">Vehicle</div><div class="fv">${vehicleDisplay}</div></div>
  <div><div class="fl">Employee</div><div class="fv">${employeeDisplay}</div></div>
  <div><div class="fl">Odometer</div><div class="fv">${inspection.odometer ? `${Number(inspection.odometer).toLocaleString()} mi` : "—"}</div></div>
</div>
<div class="sh">Summary</div>
<div class="summary-row">
  <div class="stat"><div class="stat-num" style="color:#166534">${passItems.length}</div><div class="stat-label">Pass</div></div>
  <div class="stat"><div class="stat-num" style="color:#991b1b">${failItems.length}</div><div class="stat-label">Fail</div></div>
  <div class="stat"><div class="stat-num" style="color:#6b7280">${naItems.length}</div><div class="stat-label">N/A</div></div>
  <div class="stat"><div class="stat-num">${allResponses.length}</div><div class="stat-label">Total Items</div></div>
</div>
<div class="sh">Checklist Responses</div>
<div>${printResponses.map((r) => `<div class="resp-row"><div class="resp-status" style="color:${statusColor(r)}">${statusIcon(r)}</div><div><div class="resp-name">${r.name}</div>${r.comment ? `<div class="resp-comment">"${r.comment}"</div>` : ""}</div></div>`).join("")}</div>
${inspection.notes ? `<div class="sh">Notes</div><div class="notes-box">${inspection.notes}</div>` : ""}
${
	isFailed
		? `<div class="sh" style="background:${inspection.resolvedAt ? "#166534" : "#92400e"}">${inspection.resolvedAt ? "&#10004; Resolution" : "&#9888; Unresolved"}</div>
${inspection.resolvedAt ? `<div class="grid"><div><div class="fl">Resolved Date &amp; Time</div><div class="fv">${new Date(inspection.resolvedAt).toLocaleString()}</div></div></div>${inspection.resolvedComments ? `<div class="notes-box" style="border-left-color:#166534;margin-top:4px">${inspection.resolvedComments}</div>` : ""}` : `<div class="notes-box" style="border-left-color:#b45309;color:#92400e">No resolution recorded.</div>`}`
		: ""
}
<div class="footer"><span>Asset Management · Inspection Report · ${vehicleDisplay}</span><span>Printed ${new Date().toLocaleString()}</span></div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;

		const w = window.open("", "_blank", "width=800,height=600");
		if (w) {
			w.document.write(html);
			w.document.close();
		}
	};

	const dialogTitle = `Inspection Report — ${vehicle?.unitNumber || vehicle?.label || ""}`;

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle>{dialogTitle}</DialogTitle>

				<Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
					<Tabs
						value={tab}
						onChange={(_, v) => setTab(v)}
						variant="scrollable"
						scrollButtons="auto"
					>
						<Tab label="Details" />
						<Tab label="Checklist" />
						<Tab label="Photos" />
					</Tabs>
				</Box>

				<DialogContent>
					{/* ── Tab 0: Details ── */}
					<TabPanel value={tab} index={0}>
						{/* Result header row */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1.5,
								mb: 2,
								flexWrap: "wrap",
							}}
						>
							<Typography variant="h5" component="span">
								{getResultIcon(inspection.overallStatus)}
							</Typography>
							<Chip
								label={inspection.overallStatus}
								color={getStatusChipColor(
									inspection.overallStatus,
								)}
								size="small"
								sx={{ fontWeight: 700 }}
							/>
							<Typography variant="body2" color="text.secondary">
								{moment(inspection.date).format(
									"MM/DD/YYYY hh:mm A",
								)}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								·
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{inspection.type || inspection?.typeInfo?.Code}
							</Typography>
							{inspection.odometer && (
								<>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										·
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										fontFamily="monospace"
									>
										{Number(
											inspection.odometer,
										).toLocaleString()}{" "}
										mi
									</Typography>
								</>
							)}
						</Box>

						{/* Vehicle & Employee cards */}
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(2, 1fr)",
								gap: 2,
								mb: 2,
							}}
						>
							<Box
								sx={{
									p: 1.5,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
									}}
								>
									Vehicle
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{vehicle?.label ||
										vehicle?.unitNumber ||
										"—"}
								</Typography>
							</Box>
							<Box
								sx={{
									p: 1.5,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
									}}
								>
									Employee
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{employee?.label || "—"}
								</Typography>
							</Box>
						</Box>

						{/* Summary stats */}
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								mb: 1,
								display: "block",
							}}
						>
							Summary
						</Typography>
						<Box sx={{ display: "flex", gap: 2, mb: 2 }}>
							{[
								{
									label: "Pass",
									count: passItems.length,
									color: "success.main",
								},
								{
									label: "Fail",
									count: failItems.length,
									color: "error.main",
								},
								{
									label: "N/A",
									count: naItems.length,
									color: "text.secondary",
								},
								{
									label: "Total",
									count: allResponses.length,
									color: "text.primary",
								},
							].map((stat) => (
								<Box
									key={stat.label}
									sx={{
										textAlign: "center",
										p: 1.5,
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
										minWidth: 70,
									}}
								>
									<Typography
										variant="h5"
										fontWeight={800}
										color={stat.color}
									>
										{stat.count}
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
										}}
									>
										{stat.label}
									</Typography>
								</Box>
							))}
						</Box>

						{/* Notes */}
						{inspection.notes && (
							<Box
								sx={{
									p: 1.5,
									bgcolor: "grey.50",
									borderRadius: 1,
									border: "1px solid",
									borderColor: "divider",
									mb: 2,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
									}}
								>
									Notes
								</Typography>
								<Typography variant="body2" sx={{ mt: 0.5 }}>
									{inspection.notes}
								</Typography>
							</Box>
						)}

						{/* Resolution panel */}
						{isFailed && (
							<Box
								sx={{
									borderRadius: 1,
									border: "1px solid",
									borderColor: inspection.resolvedAt
										? "success.main"
										: "warning.main",
									overflow: "hidden",
								}}
							>
								<Box
									sx={{
										px: 2,
										py: 1,
										bgcolor: inspection.resolvedAt
											? "rgba(46,125,50,0.08)"
											: "rgba(237,108,2,0.08)",
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									<Typography
										variant="caption"
										fontWeight={700}
										sx={{
											textTransform: "uppercase",
											letterSpacing: "0.06em",
											color: inspection.resolvedAt
												? "success.main"
												: "warning.main",
										}}
									>
										{inspection.resolvedAt
											? "✔ Resolved"
											: "⚠ Unresolved"}
									</Typography>
								</Box>

								{inspection.resolvedAt ? (
									<Box
										sx={{
											p: 2,
											display: "grid",
											gridTemplateColumns:
												"repeat(2, 1fr)",
											gap: "8px 16px",
										}}
									>
										<Box>
											<Typography
												variant="caption"
												fontWeight={700}
												color="text.secondary"
												sx={{
													textTransform: "uppercase",
													letterSpacing: "0.05em",
												}}
											>
												Resolved Date &amp; Time
											</Typography>
											<Typography
												variant="body2"
												fontFamily="monospace"
											>
												{moment(
													inspection.resolvedAt,
												).format("MM/DD/YYYY hh:mm A")}
											</Typography>
										</Box>
										{inspection.resolvedComments && (
											<Box
												sx={{
													gridColumn: "1 / -1",
												}}
											>
												<Typography
													variant="caption"
													fontWeight={700}
													color="text.secondary"
													sx={{
														textTransform:
															"uppercase",
														letterSpacing: "0.05em",
													}}
												>
													Comments
												</Typography>
												<Typography
													variant="body2"
													color="text.secondary"
												>
													{
														inspection.resolvedComments
													}
												</Typography>
											</Box>
										)}
									</Box>
								) : (
									<Box sx={{ p: 2 }}>
										<Typography
											variant="body2"
											color="text.secondary"
											fontStyle="italic"
										>
											No resolution recorded yet. Use the
											Resolve button to log corrective
											action.
										</Typography>
									</Box>
								)}
							</Box>
						)}
					</TabPanel>

					{/* ── Tab 1: Checklist ── */}
					<TabPanel value={tab} index={1}>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
								mb: 1,
								display: "block",
							}}
						>
							Checklist Responses ({allResponses.length})
						</Typography>
						<Box
							sx={{
								maxHeight: 400,
								overflowY: "auto",
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 1,
							}}
						>
							{allResponses.map((r, idx) => (
								<Box
									key={r.checklistItemId || idx}
									sx={{
										display: "flex",
										alignItems: "flex-start",
										justifyContent: "space-between",
										gap: 1.5,
										px: 2,
										py: 1.25,
										borderBottom:
											idx < allResponses.length - 1
												? "1px solid"
												: "none",
										borderColor: "divider",
										bgcolor:
											r.status === "Fail"
												? "rgba(211,47,47,0.04)"
												: idx % 2 === 0
													? "transparent"
													: "grey.50",
									}}
								>
									<Box sx={{ flex: 1 }}>
										<Typography
											variant="body2"
											fontWeight={600}
										>
											{r.name}
										</Typography>
										{r.comment && (
											<Typography
												variant="caption"
												color="text.secondary"
												fontStyle="italic"
												sx={{
													display: "block",
													mt: 0.25,
												}}
											>
												&ldquo;{r.comment}&rdquo;
											</Typography>
										)}
									</Box>
									<Chip
										label={r.status}
										color={getStatusChipColor(r.status)}
										size="small"
										variant="outlined"
									/>
								</Box>
							))}
							{allResponses.length === 0 && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{
										textAlign: "center",
										py: 4,
									}}
								>
									No checklist responses recorded.
								</Typography>
							)}
						</Box>
					</TabPanel>

					{/* ── Tab 2: Photos ── */}
					<TabPanel value={tab} index={2}>
						{hasPhotos ? (
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1.5,
								}}
							>
								{(Array.isArray(inspection?.photos) ? inspection.photos : [])?.map(
									(photo, idx) => (
										<Box
											key={photo.id || idx}
											sx={{
												maxWidth: "100%",
												maxHeight: 300,
												borderRadius: 1,
												border: "1px solid",
												borderColor: "divider",
											}}
										>
											<img
												src={photo.src}
												alt={photo.name}
												style={{
													maxWidth: 150,
													maxHeight: 150,
													objectFit: "cover",
												}}
											/>
										</Box>
									),
								)}
							</Box>
						) : (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									textAlign: "center",
									py: 4,
								}}
							>
								No photos attached to this inspection.
							</Typography>
						)}
					</TabPanel>
				</DialogContent>

				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					{onCreateWorkOrder && (
						<Button
							variant="outlined"
							size="small"
							onClick={() => setShowWO(true)}
						>
							🔧 Create Work Order
						</Button>
					)}
					{isFailed && onResolveInspection && (
						<Button
							variant="outlined"
							size="small"
							color={
								inspection.resolvedAt ? "success" : "warning"
							}
							onClick={() => setShowResolve(true)}
						>
							{inspection.resolvedAt
								? "✔ Edit Resolution"
								: "🔧 Resolve"}
						</Button>
					)}
					<Button
						variant="outlined"
						size="small"
						onClick={handlePrint}
					>
						🖨️ Print
					</Button>
					<PATCloseButton onClick={onClose} tooltipText="Close" />
				</DialogActions>
			</Dialog>

			{/* ── Sub-Dialogs ── */}
			<ResolveInspectionDialog
				open={showResolve}
				onClose={() => setShowResolve(false)}
				inspection={inspection}
				onSave={handleResolve}
			/>

			<CreateWorkOrderDialog
				open={showWO}
				onClose={() => setShowWO(false)}
				vehicle={vehicle}
				checklistItems={checklistItems}
				failItems={failItems}
				workOrderTypeOptions={workOrderTypeOptions}
				fundingSourceOptions={fundingSourceOptions}
				onSave={handleCreateWO}
			/>
		</>
	);
};

export default InspectionDetailDialog;
